import React, { useEffect, useMemo, useRef } from "react"
import { TableRow } from "./TableRow"
import styled from "styled-components/macro"

type TableBodyProps = {
  uniqueKey: string
  data: any[]
  cells: any[]
  selectedRows: any[]
  handleRowClick: (row: any) => void
  columns: number
  onPointerOver?: (e: any, type: any) => void
  onPointerOut?: (e: any, type: any) => void
}

const TableBody: React.FC<TableBodyProps> = ({
  uniqueKey,
  data,
  cells,
  selectedRows,
  handleRowClick,
  columns,
}) => {
  const selectedRowKeys = useMemo(
    () => new Set(selectedRows.map((row) => row.id)),
    [selectedRows, uniqueKey],
  )

  const scrollRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    if (selectedRows.length > 0) {
      const lastSelectedRow = selectedRows[selectedRows.length - 1]
      const rowElement = rowRefs.current.get(lastSelectedRow.id)
      if (rowElement) {
        rowElement.scrollIntoView({
          behavior: "smooth", // Adds smooth scrolling
          block: "center", // Centers the row in the container
        })
      }
    }
  }, [selectedRows])

  const isRowSelected = (row: any) => selectedRowKeys.has(row.id)

  return (
    <TableBodyContainer ref={scrollRef} id="table-rows-container">
      {data?.map((row: any, index: number) => (
        <div
          key={row.id}
          ref={(el) => {
            if (el) {
              rowRefs.current.set(row.id, el)
            } else {
              rowRefs.current.delete(row.id)
            }
          }}
        >
          <TableRow
            row={row}
            rowIndex={index}
            cells={cells}
            isSelected={isRowSelected(row)}
            onClick={() => handleRowClick(row)}
            columns={columns}
          />
        </div>
      ))}
    </TableBodyContainer>
  )
}

export { TableBody }

const TableBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  ::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.3);
    background-color: var(--primary);
  }

  ::-webkit-scrollbar {
    width: 2px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--secondary);
    opacity: 0.5;
  }
`
